import { _ } from "../../../core/polyglot/polyglot.js";
import { getAccountTypeFromId, income } from "./AccountType.js";
import AbstractRuleModel from "./AbstractRuleModel.js";

/**
 * Model for a manual entry. It applies a single change at a specific period on a profit and loss account,
 * providing specific settings for VAT and cash conversion.
 *
 * @param {string} name - Name of the rule
 * @param {float} value - Value of the change
 * @param {VatRate} vatRate - VAT rate instance
 * @param {int[]} paymentSpread - Numbers between 0 and 100. The sum of these
 *   numbers must be 100
 * @param {AccountType} accountType - Account type conditions how the VAT and cash conversion are calculated.
 */
export default class ManualEntryRuleModel extends AbstractRuleModel {
	constructor({
		name = "",
		value = 0,
		vatRate,
		paymentSpread = [20, 70, 10],
		accountType = income,
	} = {}) {
		super(...arguments);

		this._name = name;
		this._value = value;
		this._vatRate = vatRate;
		this._paymentSpread = paymentSpread;
		this._accountType = accountType;
	}

	getLabel() {
		return this.constructor.getLabel();
	}

	getName() {
		return this._name;
	}

	setName(name) {
		this._name = name;
	}

	getValue() {
		return this._value;
	}

	setValue(value) {
		this._value = value;
	}

	getPaymentSpread() {
		return this._paymentSpread;
	}

	setPaymentSpread(paymentSpread) {
		this._paymentSpread = paymentSpread;
	}

	getVatRate() {
		return this._vatRate;
	}

	getVatRateValue() {
		return this._vatRate?.getValue() ?? 0;
	}

	setVatRate(vatRate) {
		this._vatRate = vatRate;
	}

	getAccountType() {
		return this._accountType;
	}

	getAccountTypeName() {
		return this._accountType.getName();
	}

	setAccountType(accountType) {
		this._accountType = accountType;
	}

	isManualEntry() {
		return true;
	}

	accept(visitor) {
		return visitor.visitManualEntryRuleModel(this);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.name = this._name;
		properties.value = this._value;
		properties.accountType = this._accountType.getType();
		properties.paymentSpread = this._paymentSpread;
		properties.vatRate = this._vatRate?.getValue() ?? 0;

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.accountType = getAccountTypeFromId(data.accountType);

		data.vatRate = context.vatRates.find(
			(vatRate) => vatRate.getValue() === data.vatRate,
		);
	}

	static getDescriptionText() {
		return _("Entry that affects a specific month");
	}

	static getLabel() {
		return _("One off value");
	}

	static getType() {
		return "ManualEntry";
	}
}
